import * as THREE from "three";
import {
  Ruler3DType,
  RulerLineType,
  WallType,
} from "../../../types/floorplannerTypes";
import {
  evaluateWallAt,
  getQuadraticBezierPoint,
  getQuadraticBezierTangent,
  getWallArcLength,
} from "../../../utils/arcUtils";
import { formatLength, type LengthUnit } from "../../../utils/conversions";
import { getWall3DHeight } from "../structures/walls/wallGeometry";

export type Measurement3DKind =
  | "wall-length"
  | "wall-height"
  | "ruler-line"
  | "ruler-3d";

export type Measurement3DSpec = {
  id: string;
  kind: Measurement3DKind;
  points: THREE.Vector3[];
  capSegments: THREE.Vector3[][];
  label: string;
  labelPosition: THREE.Vector3;
  color: THREE.ColorRepresentation;
};

export type WallMeasurement3DOptions = {
  side: "left" | "right";
  wallWidth: number;
  color?: THREE.ColorRepresentation;
  /** Display unit for length labels; defaults to mm. */
  lengthUnit?: LengthUnit;
};

export type RulerMeasurement3DOptions = {
  color?: THREE.ColorRepresentation;
  /** Display unit for length labels; defaults to mm. */
  lengthUnit?: LengthUnit;
};

export type Ruler3DOrientation = "horizontal" | "vertical" | "free";

const DEFAULT_MEASUREMENT_COLOR = 0x111827;
const DEFAULT_RULER_COLOR = 0x000000;
const MIN_MEASUREMENT_LENGTH = 1e-6;
const WALL_LENGTH_OFFSET = 0.22;
const WALL_LENGTH_Z = 0.08;
const WALL_LENGTH_LABEL_Z = 0.26;
const WALL_LENGTH_CAP_SIZE = 0.12;
const WALL_HEIGHT_GAP = 0.22;
const WALL_HEIGHT_TANGENT_OFFSET = 0.18;
const WALL_HEIGHT_LABEL_OFFSET = 0.18;
const RULER_Z = 0.08;
const RULER_LABEL_Z = 0.26;
const RULER_CAP_SIZE = 0.12;
const RULER_3D_LABEL_OFFSET = 0.18;
const RULER_3D_CAP_SIZE = 0.12;
const RULER_3D_ORIENTATION_TOLERANCE = 1e-4;
const ARC_SAMPLE_COUNT = 24;
const RULER_3D_SNAP_DIRECTIONS = createRuler3DSnapDirections();

export const RULER_3D_ACTIVE_COLORS: Record<
  Ruler3DOrientation,
  THREE.ColorRepresentation
> = {
  horizontal: 0x16a34a,
  vertical: 0xdc2626,
  free: 0x2563eb,
};

export const RULER_3D_ORIENTATION_LABELS: Record<Ruler3DOrientation, string> = {
  horizontal: "Horizontal",
  vertical: "Vertical",
  free: "Free angle",
};

export function formatMeasurement3DLabel(
  worldLength: number,
  unit: LengthUnit = "mm",
): string {
  return formatLength(worldLength, unit);
}

export function createWallLengthMeasurement3D(
  wall: WallType,
  options: WallMeasurement3DOptions,
): Measurement3DSpec | null {
  const length = getWallArcLength(wall);
  if (!Number.isFinite(length) || length <= MIN_MEASUREMENT_LENGTH) return null;

  const sideSign = options.side === "left" ? 1 : -1;
  const offset = options.wallWidth / 2 + WALL_LENGTH_OFFSET;
  const z = WALL_LENGTH_Z;
  const path = createOffsetWallPath(wall, sideSign, offset, z);
  if (path.points.length < 2) return null;

  const midpoint = evaluateWallAt(wall, length / 2);
  const midpointNormal = normalForTangent(midpoint.tangent, sideSign);
  const midpoint2D = midpoint.point
    .clone()
    .addScaledVector(midpointNormal, offset);

  return {
    id: `${wall.id}:length`,
    kind: "wall-length",
    points: path.points,
    capSegments: [
      createHorizontalCap(
        path.points[0],
        path.startNormal,
        WALL_LENGTH_CAP_SIZE,
      ),
      createHorizontalCap(
        path.points[path.points.length - 1],
        path.endNormal,
        WALL_LENGTH_CAP_SIZE,
      ),
    ],
    label: formatMeasurement3DLabel(length, options.lengthUnit),
    labelPosition: new THREE.Vector3(
      midpoint2D.x,
      midpoint2D.y,
      WALL_LENGTH_LABEL_Z,
    ),
    color: options.color ?? DEFAULT_MEASUREMENT_COLOR,
  };
}

export function createWallHeightMeasurement3D(
  wall: WallType,
  options: WallMeasurement3DOptions,
): Measurement3DSpec | null {
  const height = getWall3DHeight(wall);
  if (!Number.isFinite(height) || height <= MIN_MEASUREMENT_LENGTH) return null;

  const length = getWallArcLength(wall);
  if (!Number.isFinite(length) || length <= MIN_MEASUREMENT_LENGTH) return null;

  const sideSign = options.side === "left" ? 1 : -1;
  const anchor = evaluateWallAt(wall, 0);
  const normal = normalForTangent(anchor.tangent, sideSign);
  const basePoint = anchor.point
    .clone()
    .addScaledVector(normal, options.wallWidth / 2 + WALL_HEIGHT_GAP)
    .addScaledVector(anchor.tangent, -WALL_HEIGHT_TANGENT_OFFSET);
  const bottom = new THREE.Vector3(basePoint.x, basePoint.y, 0);
  const top = new THREE.Vector3(basePoint.x, basePoint.y, height);

  return {
    id: `${wall.id}:height`,
    kind: "wall-height",
    points: [bottom, top],
    capSegments: [
      createHorizontalCap(bottom, normal, WALL_LENGTH_CAP_SIZE),
      createHorizontalCap(top, normal, WALL_LENGTH_CAP_SIZE),
    ],
    label: formatMeasurement3DLabel(height, options.lengthUnit),
    labelPosition: new THREE.Vector3(
      basePoint.x + normal.x * WALL_HEIGHT_LABEL_OFFSET,
      basePoint.y + normal.y * WALL_HEIGHT_LABEL_OFFSET,
      height / 2,
    ),
    color: options.color ?? DEFAULT_MEASUREMENT_COLOR,
  };
}

export function createRulerMeasurement3D(
  ruler: RulerLineType,
  options: RulerMeasurement3DOptions = {},
): Measurement3DSpec | null {
  const delta = new THREE.Vector2().subVectors(ruler.end, ruler.start);
  const length = delta.length();
  if (!Number.isFinite(length) || length <= MIN_MEASUREMENT_LENGTH) return null;

  const direction = delta.divideScalar(length);
  const perpendicular = new THREE.Vector2(-direction.y, direction.x);
  const start = new THREE.Vector3(ruler.start.x, ruler.start.y, RULER_Z);
  const end = new THREE.Vector3(ruler.end.x, ruler.end.y, RULER_Z);
  const midpoint = new THREE.Vector3(
    (ruler.start.x + ruler.end.x) / 2,
    (ruler.start.y + ruler.end.y) / 2,
    RULER_LABEL_Z,
  );

  return {
    id: `${ruler.id}:ruler`,
    kind: "ruler-line",
    points: [start, end],
    capSegments: [
      createHorizontalCap(start, perpendicular, RULER_CAP_SIZE),
      createHorizontalCap(end, perpendicular, RULER_CAP_SIZE),
    ],
    label: formatMeasurement3DLabel(length, options.lengthUnit),
    labelPosition: midpoint,
    color: options.color ?? DEFAULT_RULER_COLOR,
  };
}

export function createRuler3DMeasurement3D(
  ruler: Ruler3DType,
  options: RulerMeasurement3DOptions = {},
): Measurement3DSpec | null {
  const start = ruler.start.clone();
  const end = ruler.end.clone();
  const delta = new THREE.Vector3().subVectors(end, start);
  const length = delta.length();
  if (!Number.isFinite(length) || length <= MIN_MEASUREMENT_LENGTH) return null;

  const direction = delta.clone().divideScalar(length);
  const capDirection = getPerpendicularDirection3D(direction);
  const midpoint = new THREE.Vector3()
    .addVectors(start, end)
    .multiplyScalar(0.5);
  const labelPosition = midpoint
    .clone()
    .addScaledVector(capDirection, RULER_3D_LABEL_OFFSET);

  return {
    id: `${ruler.id}:ruler3D`,
    kind: "ruler-3d",
    points: [start, end],
    capSegments: [
      create3DCap(start, capDirection, RULER_3D_CAP_SIZE),
      create3DCap(end, capDirection, RULER_3D_CAP_SIZE),
    ],
    label: formatMeasurement3DLabel(length, options.lengthUnit),
    labelPosition,
    color: options.color ?? DEFAULT_RULER_COLOR,
  };
}

export function getRuler3DLength(
  ruler: Pick<Ruler3DType, "start" | "end">,
): number {
  return ruler.start.distanceTo(ruler.end);
}

export function getRuler3DDelta(
  ruler: Pick<Ruler3DType, "start" | "end">,
): THREE.Vector3 {
  return new THREE.Vector3().subVectors(ruler.end, ruler.start);
}

/**
 * Classifies alignment against the floor plane and gravity axis. A very small
 * tolerance absorbs floating-point noise from surface intersections without
 * treating visibly skewed rulers as aligned.
 */
export function getRuler3DOrientation(
  ruler: Pick<Ruler3DType, "start" | "end">,
): Ruler3DOrientation {
  const delta = getRuler3DDelta(ruler);
  if (delta.lengthSq() <= MIN_MEASUREMENT_LENGTH * MIN_MEASUREMENT_LENGTH) {
    return "free";
  }

  if (Math.hypot(delta.x, delta.y) <= RULER_3D_ORIENTATION_TOLERANCE) {
    return "vertical";
  }
  if (Math.abs(delta.z) <= RULER_3D_ORIENTATION_TOLERANCE) {
    return "horizontal";
  }
  return "free";
}

export function getRuler3DActiveColor(
  ruler: Pick<Ruler3DType, "start" | "end">,
): THREE.ColorRepresentation {
  return RULER_3D_ACTIVE_COLORS[getRuler3DOrientation(ruler)];
}

export function getRuler3DOrientationLabel(
  ruler: Pick<Ruler3DType, "start" | "end">,
): string {
  return RULER_3D_ORIENTATION_LABELS[getRuler3DOrientation(ruler)];
}

export function getRuler3DDirection(
  ruler: Pick<Ruler3DType, "start" | "end">,
): THREE.Vector3 {
  const delta = getRuler3DDelta(ruler);
  const length = delta.length();
  return length > MIN_MEASUREMENT_LENGTH
    ? delta.divideScalar(length)
    : new THREE.Vector3(1, 0, 0);
}

export function setRuler3DDistance(
  ruler: Ruler3DType,
  distance: number,
): Ruler3DType {
  const safeDistance = Math.max(distance, MIN_MEASUREMENT_LENGTH);
  const direction = getRuler3DDirection(ruler);
  return {
    ...ruler,
    measurementMode: "free",
    end: ruler.start.clone().addScaledVector(direction, safeDistance),
  };
}

export function snapRuler3DEndpointToAxisDirections(
  endpoint: THREE.Vector3,
  anchor: THREE.Vector3,
  bypassSnapping = false,
): THREE.Vector3 {
  if (bypassSnapping) return endpoint.clone();

  const delta = new THREE.Vector3().subVectors(endpoint, anchor);
  const distance = delta.length();
  if (distance <= MIN_MEASUREMENT_LENGTH) return endpoint.clone();

  const direction = delta.divideScalar(distance);
  let bestDirection = RULER_3D_SNAP_DIRECTIONS[0];
  let bestDot = -Infinity;

  RULER_3D_SNAP_DIRECTIONS.forEach((snapDirection) => {
    const dot = direction.dot(snapDirection);
    if (dot > bestDot) {
      bestDot = dot;
      bestDirection = snapDirection;
    }
  });

  return anchor.clone().addScaledVector(bestDirection, distance);
}

/**
 * Projects an endpoint onto the dominant world axis. This is the explicit
 * Shift constraint: X/Y produce a floor-horizontal ruler and Z produces a
 * gravity-vertical ruler while the dominant coordinate still follows the
 * pointer or snapped surface.
 */
export function lockRuler3DEndpointToWorldAxis(
  endpoint: THREE.Vector3,
  anchor: THREE.Vector3,
): THREE.Vector3 {
  const delta = new THREE.Vector3().subVectors(endpoint, anchor);
  const x = Math.abs(delta.x);
  const y = Math.abs(delta.y);
  const z = Math.abs(delta.z);

  if (z >= x && z >= y) {
    return anchor.clone().add(new THREE.Vector3(0, 0, delta.z));
  }
  if (x >= y) {
    return anchor.clone().add(new THREE.Vector3(delta.x, 0, 0));
  }
  return anchor.clone().add(new THREE.Vector3(0, delta.y, 0));
}

export function snapRulerEndpointTo45Degrees(
  endpoint: THREE.Vector2,
  anchor: THREE.Vector2,
  angleSnapTolerance: number,
  bypassSnapping = false,
): THREE.Vector2 {
  if (bypassSnapping || !Number.isFinite(angleSnapTolerance)) {
    return endpoint.clone();
  }

  const distance = endpoint.distanceTo(anchor);
  if (distance <= MIN_MEASUREMENT_LENGTH) return endpoint.clone();

  const angle = Math.atan2(endpoint.y - anchor.y, endpoint.x - anchor.x);
  const normalizedAngle = (THREE.MathUtils.radToDeg(angle) + 360) % 360;
  const snappedAngleDegrees = Math.round(normalizedAngle / 45) * 45;
  const snappedAngle = THREE.MathUtils.degToRad(snappedAngleDegrees);
  const snappedEndpoint = new THREE.Vector2(
    anchor.x + distance * Math.cos(snappedAngle),
    anchor.y + distance * Math.sin(snappedAngle),
  );

  return endpoint.distanceTo(snappedEndpoint) < angleSnapTolerance
    ? snappedEndpoint
    : endpoint.clone();
}

function createOffsetWallPath(
  wall: WallType,
  sideSign: 1 | -1,
  offset: number,
  z: number,
): {
  points: THREE.Vector3[];
  startNormal: THREE.Vector2;
  endNormal: THREE.Vector2;
} {
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    const tangent = new THREE.Vector2().subVectors(wall.end, wall.start);
    const tangentLength = tangent.length();
    if (tangentLength <= MIN_MEASUREMENT_LENGTH) {
      return {
        points: [],
        startNormal: new THREE.Vector2(0, 1),
        endNormal: new THREE.Vector2(0, 1),
      };
    }
    tangent.divideScalar(tangentLength);
    const normal = normalForTangent(tangent, sideSign);
    return {
      points: [
        point2DToVector3(wall.start.clone().addScaledVector(normal, offset), z),
        point2DToVector3(wall.end.clone().addScaledVector(normal, offset), z),
      ],
      startNormal: normal,
      endNormal: normal,
    };
  }

  const points: THREE.Vector3[] = [];
  let startNormal = new THREE.Vector2(0, 1);
  let endNormal = new THREE.Vector2(0, 1);
  for (let index = 0; index <= ARC_SAMPLE_COUNT; index++) {
    const t = index / ARC_SAMPLE_COUNT;
    const point = getQuadraticBezierPoint(
      t,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
    const tangent = getQuadraticBezierTangent(
      t,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
    const normalizedTangent =
      tangent.length() > MIN_MEASUREMENT_LENGTH
        ? tangent.normalize()
        : new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
    const normal = normalForTangent(normalizedTangent, sideSign);
    if (index === 0) startNormal = normal;
    if (index === ARC_SAMPLE_COUNT) endNormal = normal;
    points.push(point2DToVector3(point.addScaledVector(normal, offset), z));
  }
  return { points, startNormal, endNormal };
}

function normalForTangent(
  tangent: THREE.Vector2,
  sideSign: 1 | -1,
): THREE.Vector2 {
  const normalized =
    tangent.length() > MIN_MEASUREMENT_LENGTH
      ? tangent.clone().normalize()
      : new THREE.Vector2(1, 0);
  const left = new THREE.Vector2(-normalized.y, normalized.x);
  return sideSign === 1 ? left : left.negate();
}

function createHorizontalCap(
  center: THREE.Vector3,
  direction: THREE.Vector2,
  length: number,
): THREE.Vector3[] {
  const capDirection =
    direction.length() > MIN_MEASUREMENT_LENGTH
      ? direction.clone().normalize()
      : new THREE.Vector2(0, 1);
  return [
    new THREE.Vector3(
      center.x + capDirection.x * length,
      center.y + capDirection.y * length,
      center.z,
    ),
    new THREE.Vector3(
      center.x - capDirection.x * length,
      center.y - capDirection.y * length,
      center.z,
    ),
  ];
}

function create3DCap(
  center: THREE.Vector3,
  direction: THREE.Vector3,
  length: number,
): THREE.Vector3[] {
  const capDirection =
    direction.length() > MIN_MEASUREMENT_LENGTH
      ? direction.clone().normalize()
      : new THREE.Vector3(1, 0, 0);
  return [
    center.clone().addScaledVector(capDirection, length),
    center.clone().addScaledVector(capDirection, -length),
  ];
}

function getPerpendicularDirection3D(direction: THREE.Vector3): THREE.Vector3 {
  const normalized =
    direction.length() > MIN_MEASUREMENT_LENGTH
      ? direction.clone().normalize()
      : new THREE.Vector3(0, 0, 1);
  const reference =
    Math.abs(normalized.z) < 0.9
      ? new THREE.Vector3(0, 0, 1)
      : new THREE.Vector3(1, 0, 0);
  return new THREE.Vector3().crossVectors(normalized, reference).normalize();
}

function createRuler3DSnapDirections(): THREE.Vector3[] {
  const directions: THREE.Vector3[] = [];
  [-1, 0, 1].forEach((x) => {
    [-1, 0, 1].forEach((y) => {
      [-1, 0, 1].forEach((z) => {
        if (x === 0 && y === 0 && z === 0) return;
        directions.push(new THREE.Vector3(x, y, z).normalize());
      });
    });
  });
  return directions;
}

function point2DToVector3(point: THREE.Vector2, z: number): THREE.Vector3 {
  return new THREE.Vector3(point.x, point.y, z);
}
